// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Stack, useMediaQuery, useTheme, Tabs, Tab, Box, Typography,
  Card, CardContent, Grid, Button, Divider, TextField, Select,
  MenuItem, InputLabel, FormControl, Chip, Paper, CircularProgress, LinearProgress, alpha,
  Checkbox, FormControlLabel,
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useForm, Controller } from "react-hook-form";
import { MerchantSelector, ComboboxWithFreeText, ComboboxState } from "../../atoms";
import { Table } from "../../molecules";
import {
  useGetActiveGMUStores, IGMUStore,
  useGetMerchantList,
  useGetGibUserList,
  useUpdateMerchantIntegratorInformation,
  useCreateGMUCustomer,
} from "../../../hooks";
import { TerminalDevice } from "./TerminalDevice";
import { MerchantSoftPOSAuthorizationForm } from "../merchant/MerchantSoftPOSAuthorizationForm";
import { useSetSnackBar } from "../../../store/Snackbar.state";

type SelectedMerchant = { merchantId: number; merchantName: string; taxNumber: string };

type IntegratorFormValues = {
  integratorId: number;
  integratorUid: string;
  integratorPwd: string;
  integratorGbAlias: string;
  integratorEArchiveGbAlias: string;
};

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Card variant="outlined" sx={{ height: "100%", borderRadius: 2, overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.05) : alpha("#000", 0.04),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          {title}
        </Typography>
      </Box>
      <CardContent sx={{ pt: 1.5 }}>{children}</CardContent>
    </Card>
  );
}

function DetailRow({ label, value, index }: { label: string; value?: string | number | null; index?: number }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: index !== undefined && index % 2 === 0 ? alpha(theme.palette.action.hover, 0.04) : "transparent",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 170, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value != null && value !== "" ? value : <span style={{ color: theme.palette.text.disabled }}>—</span>}
      </Typography>
    </Box>
  );
}

function GMUStoreReadCard({ store, taxNumber }: { store: IGMUStore; taxNumber?: string }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <SectionCard title="Mağaza Bilgileri" icon={<StoreIcon fontSize="small" />}>
          <DetailRow index={0} label="Merchant ID" value={store.merchantId} />
          <DetailRow index={1} label="Merchant Adı" value={store.merchantName} />
          <DetailRow index={2} label="Vergi Numarası" value={taxNumber} />
          <DetailRow index={3} label="GMU Mağaza ID" value={store.gmuStoreId} />
          <DetailRow index={4} label="GMU Tenant ID" value={store.gmuTenantId} />
          <DetailRow
            index={5}
            label="Oluşturma Tarihi"
            value={store.storeCreateDate ? new Date(store.storeCreateDate).toLocaleString("tr-TR") : undefined}
          />
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <SectionCard title="Entegratör Bilgileri" icon={<IntegrationInstructionsIcon fontSize="small" />}>
          <DetailRow index={0} label="Entegratör" value="ICE Teknoloji" />
          <DetailRow index={1} label="Entegratör Kullanıcı" value={store.integratorUid} />
          <DetailRow index={2} label="Entegratör Şifre" value={store.integratorPwd} />
          <DetailRow index={3} label="E-Fatura GB Alias" value={store.integratorGbAlias} />
          <DetailRow index={4} label="E-Arşiv GB Alias" value={store.integratorEArchiveGbAlias} />
        </SectionCard>
      </Grid>
    </Grid>
  );
}

function GMUStoreEditCard({
  store,
  merchantId,
  taxNumber,
  onSaved,
}: {
  store: IGMUStore;
  merchantId: number;
  taxNumber: string;
  onSaved: () => void;
}) {
  const setSnackBar = useSetSnackBar();
  const theme = useTheme();
  const { mutate: updateIntegrator, isLoading: isSaving } = useUpdateMerchantIntegratorInformation();
  const { mutate: createGMUCustomer, isLoading: isSendingToGMU } = useCreateGMUCustomer();
  const { mutate: rawGbAliasList, isLoading: isGbAliasLoading } = useGetGibUserList();
  const [preValidate, setPreValidate] = useState(false);
  const [gmuSendResult, setGmuSendResult] = useState<{ isSuccess: boolean; message: string; errorCode: string | null; requestId: string } | null>(null);
  const [gbAliasList, setGbAliasList] = useState<{ label: string; value: string }[]>([]);
  const [isGbAliasValid, setIsGbAliasValid] = useState(true);
  const [isEArchiveGbAliasValid, setIsEArchiveGbAliasValid] = useState(true);

  const { control, register, handleSubmit, watch, reset } = useForm<IntegratorFormValues>({
    defaultValues: {
      integratorId: store.integratorId || 30,
      integratorUid: store.integratorUid ?? "",
      integratorPwd: store.integratorPwd ?? "",
      integratorGbAlias: store.integratorGbAlias ?? "",
      integratorEArchiveGbAlias: store.integratorEArchiveGbAlias ?? "",
    },
  });

  const integratorGbAlias = watch("integratorGbAlias");
  const integratorEArchiveGbAlias = watch("integratorEArchiveGbAlias");

  useEffect(() => {
    reset({
      integratorId: store.integratorId || 30,
      integratorUid: store.integratorUid ?? "",
      integratorPwd: store.integratorPwd ?? "",
      integratorGbAlias: store.integratorGbAlias ?? "",
      integratorEArchiveGbAlias: store.integratorEArchiveGbAlias ?? "",
    });
  }, [store, reset]);

  useEffect(() => {
    if (taxNumber?.length === 10) {
      rawGbAliasList(
        { taxNumber },
        {
          onSuccess: (data) => {
            setGbAliasList((data?.data || []).map((item: string) => ({ label: item, value: item })));
          },
          onError: () => setGbAliasList([]),
        }
      );
    } else {
      setGbAliasList([]);
    }
  }, [taxNumber, rawGbAliasList]);

  useEffect(() => {
    if (integratorGbAlias && gbAliasList.length > 0) {
      setIsGbAliasValid(gbAliasList.some((item) => item.value === integratorGbAlias));
    }
  }, [integratorGbAlias, gbAliasList]);

  useEffect(() => {
    if (integratorEArchiveGbAlias && gbAliasList.length > 0) {
      setIsEArchiveGbAliasValid(gbAliasList.some((item) => item.value === integratorEArchiveGbAlias));
    }
  }, [integratorEArchiveGbAlias, gbAliasList]);

  const onSubmit = (values: IntegratorFormValues) => {
    updateIntegrator(
      { ...values, merchantId },
      {
        onSuccess: () => {
          setSnackBar({ severity: "success", description: "Entegratör bilgileri güncellendi." });
          onSaved();
        },
        onError: (err: any) => {
          setSnackBar({ severity: "error", description: err?.message || "Güncelleme başarısız." });
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Mağaza Bilgileri" icon={<StoreIcon fontSize="small" />}>
            <DetailRow index={0} label="Merchant ID" value={store.merchantId} />
            <DetailRow index={1} label="Merchant Adı" value={store.merchantName} />
            <DetailRow index={2} label="Vergi Numarası" value={taxNumber} />
            <DetailRow index={3} label="GMU Mağaza ID" value={store.gmuStoreId || undefined} />
            <DetailRow index={4} label="GMU Tenant ID" value={store.gmuTenantId || undefined} />
            <DetailRow
              index={5}
              label="Oluşturma Tarihi"
              value={store.storeCreateDate ? new Date(store.storeCreateDate).toLocaleString("tr-TR") : undefined}
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Entegratör Bilgileri" icon={<IntegrationInstructionsIcon fontSize="small" />}>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Fatura Entegratör</InputLabel>
                <Controller
                  name="integratorId"
                  control={control}
                  defaultValue={30}
                  render={({ field: { onChange, value } }) => (
                    <Select value={value} onChange={onChange} disabled label="Fatura Entegratör">
                      {[{ label: "ICE Teknoloji", value: 30 }].map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <TextField
                {...register("integratorUid")}
                label="Entegratör Kullanıcı"
                size="small"
                fullWidth
              />
              <TextField
                {...register("integratorPwd")}
                label="Entegratör Şifre"
                size="small"
                fullWidth
              />
              <ComboboxWithFreeText
                control={control}
                name="integratorGbAlias"
                label="E-Fatura GB Alias"
                options={gbAliasList}
                isLoading={isGbAliasLoading}
                state={isGbAliasValid ? ComboboxState.NO_ACTION : ComboboxState.WARNING}
                stateText="Girilen değer listedeki değerlerden biri değil."
              />
              <ComboboxWithFreeText
                control={control}
                name="integratorEArchiveGbAlias"
                label="E-Arşiv GB Alias"
                options={gbAliasList}
                isLoading={isGbAliasLoading}
                state={isEArchiveGbAliasValid ? ComboboxState.NO_ACTION : ComboboxState.WARNING}
                stateText="Girilen değer listedeki değerlerden biri değil."
              />
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
              sx={{ minWidth: 120 }}
            >
              {isSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </Stack>
        </Grid>

        {store.integratorUid && store.integratorPwd && store.integratorGbAlias && (
          <Grid item xs={12}>
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderBottom: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.15),
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                  GMU'ya Gönder
                </Typography>
              </Box>

              {/* Body */}
              <Box sx={{ p: 2.5, bgcolor: "background.paper" }}>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Entegratör bilgileri kaydedildi. Üye işyerini GMU sistemine göndermek için aşağıdaki butonu kullanın.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={preValidate}
                        onChange={(e) => setPreValidate(e.target.checked)}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.primary">
                        Ön Onay — Göndermeden önce doğrulama yap
                      </Typography>
                    }
                  />

                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isSendingToGMU}
                      startIcon={isSendingToGMU ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
                      onClick={() => {
                        setGmuSendResult(null);
                        createGMUCustomer(
                          { merchantId, preValidate },
                          {
                            onSuccess: (res: any) => {
                              const result = {
                                isSuccess: res?.isSuccess ?? false,
                                message: res?.message ?? "",
                                errorCode: res?.errorCode ?? null,
                                requestId: res?.requestId ?? "",
                              };
                              setGmuSendResult(result);
                              if (result.isSuccess) onSaved();
                            },
                            onError: (err: any) => {
                              setGmuSendResult({
                                isSuccess: false,
                                message: err?.message || "GMU gönderimi başarısız.",
                                errorCode: null,
                                requestId: "",
                              });
                            },
                          }
                        );
                      }}
                      sx={{ minWidth: 220 }}
                    >
                      {isSendingToGMU ? "Gönderiliyor..." : "Üye İşyerini GMU'ya Gönder"}
                    </Button>
                  </Stack>

                  {gmuSendResult && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: gmuSendResult.isSuccess ? "#a8d5b5" : "#e8b4b8",
                        bgcolor: gmuSendResult.isSuccess ? "#f0faf2" : "#fdf3f3",
                      }}
                    >
                      <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="flex-start" gap={1}>
                          <Chip
                            size="small"
                            label={gmuSendResult.isSuccess ? "Başarılı" : "Başarısız"}
                            sx={
                              gmuSendResult.isSuccess
                                ? { fontWeight: 700, fontSize: "0.7rem", bgcolor: "#d4edda", color: "#155724", border: "1px solid #a8d5b5", flexShrink: 0 }
                                : { fontWeight: 700, fontSize: "0.7rem", bgcolor: "#f8d7da", color: "#721c24", border: "1px solid #e8b4b8", flexShrink: 0 }
                            }
                          />
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                            {gmuSendResult.message}
                          </Typography>
                        </Stack>
                        {gmuSendResult.errorCode && (
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                            Hata Kodu: <strong>{gmuSendResult.errorCode}</strong>
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </form>
  );
}

export function GMUSoftPosControl() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const tableWidth = isDesktop ? 1308 : window.innerWidth - 50;
  const setSnackBar = useSetSnackBar();

  const [topTab, setTopTab] = useState(0);

  const { mutate: getMerchantList, isLoading: isMerchantListLoading } = useGetMerchantList();
  const [merchantOptions, setMerchantOptions] = useState<{ value: number; label: string }[]>([]);
  const [merchantMap, setMerchantMap] = useState<Record<number, SelectedMerchant>>({});
  const [selectedMerchant, setSelectedMerchant] = useState<SelectedMerchant | null>(null);
  const [merchantHasMore, setMerchantHasMore] = useState(false);
  const merchantPageRef = useRef(0);
  const merchantQueryRef = useRef('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const PAGE_SIZE = 25;

  const fetchMerchants = useCallback(
    (page: number, query: string, append: boolean) => {
      const isNumeric = /^\d+$/.test(query.trim());
      const params: any = { page, size: PAGE_SIZE };
      if (query.trim()) {
        if (isNumeric) params.merchantId = query.trim();
        else params.merchantName = query.trim();
      }
      getMerchantList(params, {
        onSuccess: (data) => {
          const paging = data.data;
          const result: any[] = paging?.result || [];
          const map: Record<number, SelectedMerchant> = {};
          result.forEach((m: any) => {
            map[m.merchantId] = { merchantId: m.merchantId, merchantName: m.merchantName, taxNumber: m.taxNumber };
          });
          setMerchantMap((prev) => ({ ...prev, ...map }));
          const newOptions = result.map((m: any) => ({ value: m.merchantId, label: `${m.merchantId} - ${m.merchantName}` }));
          setMerchantOptions((prev) => append ? [...prev, ...newOptions] : newOptions);
          merchantPageRef.current = page;
          setMerchantHasMore(page + 1 < (paging?.totalPages ?? 0));
        },
      });
    },
    [getMerchantList]
  );

  useEffect(() => {
    fetchMerchants(0, '', false);
  }, [fetchMerchants]);

  const handleMerchantSearch = useCallback((query: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    merchantQueryRef.current = query;
    searchDebounceRef.current = setTimeout(() => {
      fetchMerchants(0, query, false);
    }, 350);
  }, [fetchMerchants]);

  const handleMerchantLoadMore = useCallback(() => {
    if (isMerchantListLoading || !merchantHasMore) return;
    fetchMerchants(merchantPageRef.current + 1, merchantQueryRef.current, true);
  }, [fetchMerchants, isMerchantListLoading, merchantHasMore]);

  const { mutate: getMerchantGMUDetail, isLoading: isDetailLoading } = useGetActiveGMUStores();
  const [gmuStore, setGmuStore] = useState<IGMUStore | null>(null);
  const [noGMURecord, setNoGMURecord] = useState(false);

  const [bottomTab, setBottomTab] = useState(0);
  const [isSendingToGMU, setIsSendingToGMU] = useState(false);

  const { mutate: getAllGMUStores, isLoading: isListingLoading } = useGetActiveGMUStores();
  const [listingData, setListingData] = useState<any>(null);
  const [listingPagination, setListingPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  useEffect(() => {
    if (!selectedMerchant) return;
    setGmuStore(null);
    setNoGMURecord(false);
    getMerchantGMUDetail(
      { pageNumber: 1, pageSize: 1, merchantId: selectedMerchant.merchantId },
      {
        onSuccess: (data) => {
          const result = data.data?.result;
          if (result && result.length > 0) {
            setGmuStore(result[0]);
          } else {
            setNoGMURecord(true);
          }
        },
      }
    );
  }, [selectedMerchant, getMerchantGMUDetail]);

  const handleMerchantChange = (val: number | null) => {
    setSelectedMerchant(val ? merchantMap[val] ?? null : null);
    setGmuStore(null);
    setNoGMURecord(false);
    setBottomTab(0);
  };

  const refetchGMUDetail = () => {
    if (!selectedMerchant) return;
    getMerchantGMUDetail(
      { pageNumber: 1, pageSize: 1, merchantId: selectedMerchant.merchantId },
      {
        onSuccess: (data) => {
          const result = data.data?.result;
          setGmuStore(result && result.length > 0 ? result[0] : null);
        },
      }
    );
  };

  useEffect(() => {
    if (topTab !== 1) return;
    getAllGMUStores(
      { pageNumber: listingPagination.page + 1, pageSize: listingPagination.pageSize, onlyActiveStores: true },
      { onSuccess: (data) => setListingData(data.data) }
    );
  }, [topTab, listingPagination.page, listingPagination.pageSize, getAllGMUStores]);

  const isGMUActive = gmuStore && gmuStore.gmuTenantId != null && gmuStore.gmuTenantId !== 0;

  const listingColumns: GridColDef<IGMUStore>[] = [
    { field: "merchantId", headerName: "Merchant ID", width: 120 },
    { field: "merchantName", headerName: "Merchant Adı", flex: 1, minWidth: 200 },
    { field: "gmuStoreId", headerName: "GMU Mağaza ID", width: 290 },
    { field: "integratorUid", headerName: "Entegratör Kullanıcı", width: 160 },
    { field: "integratorGbAlias", headerName: "GB Alias", width: 220 },
    {
      field: "storeCreateDate",
      headerName: "Oluşturma Tarihi",
      width: 180,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleString("tr-TR") : "",
    },
  ];

  const isTab0Loading = isMerchantListLoading || isDetailLoading;
  const isTab1Loading = isListingLoading;

  const gmuStatusChip = selectedMerchant && !isDetailLoading && (() => {
    if (isGMUActive) return (
      <Chip
        size="small"
        label="GMU Aktif"
        sx={{ fontWeight: 600, bgcolor: "#e6f4ea", color: "#1e7e34", border: "1px solid #a8d5b5" }}
      />
    );
    if (noGMURecord) return (
      <Chip
        size="small"
        label="GMU Kaydı Yok"
        sx={{ fontWeight: 600, bgcolor: "#f3f3f3", color: "#555", border: "1px solid #ccc" }}
      />
    );
    return (
      <Chip
        size="small"
        label="GMU Pasif"
        sx={{ fontWeight: 600, bgcolor: "#fff4e5", color: "#b26a00", border: "1px solid #f5c78e" }}
      />
    );
  })();

  return (
    <Stack flex={1} p={2}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={topTab} onChange={(_, v) => setTopTab(v)}>
          <Tab label="Üye İşyeri Seçimi" />
          <Tab label="Üye İşyeri Listesi" />
        </Tabs>
      </Box>

      {topTab === 0 && (
        <Box sx={{ flex: 1 }}>
          {isTab0Loading && <LinearProgress sx={{ height: 2 }} />}
          <Stack flex={1} p={3} spacing={3}>
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.03) }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                    Üye İşyeri Seç
                  </Typography>
                  {gmuStatusChip}
                </Stack>
                <MerchantSelector
                  multiple={false}
                  merchantList={merchantOptions}
                  field={{ value: selectedMerchant?.merchantId ?? null, onChange: handleMerchantChange }}
                  onSearch={handleMerchantSearch}
                  onLoadMore={handleMerchantLoadMore}
                  isLoading={isMerchantListLoading}
                  hasMore={merchantHasMore}
                />
              </Stack>
            </Paper>

            {gmuStore && isGMUActive && (
              <Stack spacing={3}>
                <GMUStoreReadCard store={gmuStore} taxNumber={selectedMerchant?.taxNumber} />
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}>
                    <Tabs value={bottomTab} onChange={(_, v) => setBottomTab(v)}>
                      <Tab label="Cihazlar" />
                      <Tab label="Bankalar" />
                    </Tabs>
                  </Box>
                  <Box p={2}>
                    {bottomTab === 0 && <TerminalDevice merchantId={selectedMerchant?.merchantId} />}
                    {bottomTab === 1 && <MerchantSoftPOSAuthorizationForm merchantId={selectedMerchant?.merchantId} />}
                  </Box>
                </Paper>
              </Stack>
            )}

            {gmuStore && !isGMUActive && (
              <GMUStoreEditCard
                store={gmuStore}
                merchantId={selectedMerchant!.merchantId}
                taxNumber={selectedMerchant!.taxNumber}
                onSaved={refetchGMUDetail}
              />
            )}

            {noGMURecord && !isDetailLoading && (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 2,
                  textAlign: "center",
                  borderStyle: "dashed",
                  bgcolor: alpha(theme.palette.warning.main, 0.03),
                  borderColor: alpha(theme.palette.warning.main, 0.4),
                }}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  GMU Kaydı Bulunamadı
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  <strong>{selectedMerchant?.merchantName}</strong> üye işyeri henüz GMU'ya kayıtlı değil.
                  Aşağıdaki butona tıklayarak kaydı başlatabilirsiniz.
                </Typography>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  disabled={isSendingToGMU}
                  startIcon={isSendingToGMU ? <CircularProgress size={18} color="inherit" /> : <CloudUploadOutlinedIcon />}
                  onClick={() => {
                    setIsSendingToGMU(true);
                    setTimeout(() => {
                      setIsSendingToGMU(false);
                      setSnackBar({ severity: "success", description: "Üye işyeri GMU'ya başarıyla gönderildi." });
                      refetchGMUDetail();
                    }, 1500);
                  }}
                  sx={{ minWidth: 240 }}
                >
                  {isSendingToGMU ? "Gönderiliyor..." : "Üye İşyerini GMU'ya Gönder"}
                </Button>
              </Paper>
            )}

            {!selectedMerchant && (
              <Paper
                variant="outlined"
                sx={{ p: 4, borderRadius: 2, textAlign: "center", borderStyle: "dashed", bgcolor: "transparent" }}
              >
                <StoreIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  GMU bilgilerini görüntülemek için lütfen bir üye işyeri seçin.
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      )}

      {topTab === 1 && (
        <Box>
          {isTab1Loading && <LinearProgress sx={{ height: 2 }} />}
          <Box p={3}>
            <Table
              columnVisibilityConfigKey="gmuSoftPosListingColumnVisibilityKey"
              paginationModel={listingPagination}
              onPaginationModelChange={setListingPagination}
              paginationMode="server"
              rowCount={listingData?.totalItems || 0}
              sx={{ width: tableWidth }}
              isRowSelectable={() => false}
              rows={listingData?.result || []}
              columns={listingColumns}
              exportFileName="GMU SoftPos Merchant Listesi"
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
}

export default GMUSoftPosControl;
